import { CardShell } from '@atlas/ui-components'
import { Badge, Button, Icon } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ChipSet } from '@/components/classification/ChipSet'
import { AddAxisButton } from './AddAxisButton'
import { AxisControl } from './AxisControl'
import { DEFAULT_AXES } from './classify-config'
import { type ClassifyRowView, detectedTypeLabel } from './row-view'
import type { PanelAxis } from './types'
import { useRevealedAxes } from './useRevealedAxes'

export interface ClassifyCardProps {
	row: ClassifyRowView
	/** Axes a file cannot commit without. */
	requiredAxes: PanelAxis[]
	/** Everything else, revealed only when this file is being overridden. */
	optionalAxes: PanelAxis[]
	/** Human labels per axis code, for the read-only chips. */
	axisLabels: Record<string, string>
	showErrors: boolean
	onOverride: (stagedId: string, axisCode: string, valueCode: string) => void
	onRemove: (stagedId: string) => void
}

/** The file's own state: what it needs, or that it is good to go. */
function CardStatus({ row, axisLabels }: { row: ClassifyRowView; axisLabels: Record<string, string> }) {
	// An upload failure outranks readiness: the row is classified fine, the bytes
	// just didn't land, and re-committing retries it.
	if (row.error) {
		return (
			<Badge variant="danger" size="sm" title={row.error}>
				upload failed
			</Badge>
		)
	}
	if (row.readiness.ready) {
		return (
			<Badge variant="success" size="sm">
				ready
			</Badge>
		)
	}
	return (
		<span className="flex flex-wrap items-center gap-ds-2">
			<Badge variant="danger" size="sm" className="whitespace-nowrap">
				held back
			</Badge>
			{/* Named, not hidden in a tooltip — "held back" says the file will not
			    commit but not what to do about it. Labelled, not coded: the picker
			    the user must go fix is titled "Asset Type", not "asset_type". */}
			<span className="text-ds-xs text-ds-text-muted">
				Needs {row.readiness.missing.map((code) => axisLabels[code] ?? code).join(', ')}
			</span>
		</span>
	)
}

/**
 * One staged file, as a card.
 *
 * Replaces a table row because the work is per-file editing, not column
 * scanning: three comboboxes need ~180px each, which no table cell competing
 * with File / Name / Status can give them — the previous layout truncated
 * values to "U" and "Bl" while showing only 2 of 14 axes.
 *
 * The classification is READ-ONLY by default. The rail above already set it for
 * the whole batch, and most batches never diverge, so a card shows chips and
 * opens pickers only for the file the user actually wants to change. That is
 * also what removes the width problem: text wraps, controls do not.
 */
export function ClassifyCard({
	row,
	requiredAxes,
	optionalAxes,
	axisLabels,
	showErrors,
	onOverride,
	onRemove,
}: ClassifyCardProps) {
	// `null` = follow the auto-open rule; a boolean = the user has said otherwise.
	// A plain boolean could not express "collapse a card that opened itself", so
	// Done was a dead button on every held-back card after a failed commit.
	const [override, setOverride] = useState<boolean | null>(null)
	// Held-back files open their pickers unprompted once errors are revealed:
	// the user has to act on them, so making them hunt for "Override" first
	// would be busywork.
	const autoOpen = showErrors && !row.readiness.ready && !row.error
	const isEditing = override ?? autoOpen

	// Same reveal rule as the batch rail. The registry holds 15 dimensions and
	// grows by INSERT, so opening every axis at once would make one card taller
	// than the viewport — and taller again with each dimension added.
	const allAxes = useMemo(() => [...requiredAxes, ...optionalAxes], [requiredAxes, optionalAxes])
	const requiredAxisCodes = useMemo(() => requiredAxes.map((axis) => axis.code), [requiredAxes])
	const { shown, hidden, reveal } = useRevealedAxes(allAxes, requiredAxisCodes, row.classification, DEFAULT_AXES)

	// Chips show the option's LABEL, not its code — the rail above says
	// "Bravecto", and a card reading "bravecto" would look like a different value.
	const chips = Object.fromEntries(
		Object.entries(row.classification)
			.filter(([, value]) => value !== '')
			.map(([axisCode, value]) => {
				const axis = [...requiredAxes, ...optionalAxes].find((candidate) => candidate.code === axisCode)
				return [axisCode, [axis?.options.find((option) => option.value === value)?.label ?? value]]
			}),
	)

	return (
		/* `min-w-0`: a grid track sizes to `auto` by default, so without it a long
		   file name widens the whole column instead of truncating inside it. */
		<CardShell as="li" className="flex min-w-0 flex-col gap-ds-3 bg-ds-surface p-ds-3">
			<div className="flex items-start gap-ds-3">
				{row.previewUrl ? (
					<img
						src={row.previewUrl}
						alt={`Preview of ${row.fileName}`}
						className="h-12 w-12 shrink-0 rounded-ds-sm object-cover"
					/>
				) : (
					<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-ds-sm border border-ds-border bg-ds-surface-elevated font-ds-mono text-ds-xs text-ds-text-subtle">
						{detectedTypeLabel(row.contentType)}
					</span>
				)}
				{/* `min-w-0` so a long file name truncates instead of widening the card
				    past its grid track. */}
				<span className="min-w-0 flex-1 truncate text-ds-sm text-ds-text" title={row.fileName}>
					{row.fileName}
				</span>
				<Button variant="muted" onPress={() => onRemove(row.stagedId)} aria-label={`Remove ${row.fileName}`}>
					<Icon size="sm">
						<X />
					</Icon>
				</Button>
			</div>

			{isEditing ? (
				<div className="flex flex-col gap-ds-2">
					{shown.map((axis) => (
						<AxisControl
							key={axis.code}
							axis={axis}
							value={row.classification[axis.code] ?? ''}
							onChange={(value) => onOverride(row.stagedId, axis.code, value)}
							scope={row.fileName}
							isRequired={requiredAxisCodes.includes(axis.code)}
							showError={showErrors}
							placeholder="—"
						/>
					))}
					<AddAxisButton hidden={hidden} onAdd={reveal} ariaContext={`for ${row.fileName}`} size="sm" />
				</div>
			) : (
				<ChipSet tuple={chips} axisLabels={axisLabels} />
			)}

			<div className="flex items-center justify-between gap-ds-2">
				<CardStatus row={row} axisLabels={axisLabels} />
				<Button variant="muted" onPress={() => setOverride(!isEditing)}>
					{isEditing ? 'Done' : 'Override'}
				</Button>
			</div>
		</CardShell>
	)
}
